import type { ComponentProps, ReactNode } from 'react';
import { Button } from '@/components/ui/button.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.js';
import { cn } from '@/lib/utils.js';

export type IconButtonProps = Omit<ComponentProps<typeof Button>, 'children' | 'size' | 'asChild'> & {
  /** Names the action. Becomes the aria-label, and the tooltip text unless
   *  `tooltip` overrides it, so the icon can never be the only thing telling
   *  the operator what happens. */
  label: string;
  /**
   * A shorter tooltip, when `label` has to carry more than a tooltip should.
   *
   * Inside a table, `label` must name the ROW as well as the action — "Delete"
   * on eight hundred rows names nothing for a screen reader, and
   * `getByRole('button', { name })` cannot tell them apart either. But the
   * operator hovering that button can already see which row they are on, so
   * showing them the whole URL again puts a paragraph under the pointer. When
   * the two needs disagree, `label` serves the screen reader and this serves
   * the eye.
   */
  tooltip?: string;
  children: ReactNode;
  /** Which side the tooltip opens on. Rows near the viewport edge want 'left'. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Set for a toggle so assistive technology reads the on/off state. Also
   *  drives the pressed treatment, so on/off is visible as well as announced. */
  pressed?: boolean;
};

/**
 * A square, icon-only button with an instant tooltip.
 *
 * `delayDuration` is 0 by default in `Tooltip`: a table row of unlabelled
 * glyphs is only usable if naming one is immediate.
 *
 * When `pressed` is given the button becomes a toggle. The on state is filled
 * with the accent tint *and* carries `aria-pressed`, so the state survives
 * both a screen reader and a monochrome screen.
 */
export function IconButton({
  label,
  tooltip,
  children,
  side = 'top',
  pressed,
  variant = 'ghost',
  className,
  ...props
}: IconButtonProps) {
  const isToggle = pressed !== undefined;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant={variant}
          aria-label={label}
          {...(isToggle ? { 'aria-pressed': pressed } : {})}
          className={cn(
            'shrink-0',
            isToggle && 'border border-rule-strong',
            pressed === true && 'bg-accent-quiet text-accent hover:bg-accent-quiet',
            className,
          )}
          {...props}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent side={side}>{tooltip ?? label}</TooltipContent>
    </Tooltip>
  );
}
